/**
 * scheduleSkillMatrix.ts
 * Frontend service for the Employee Skill Matrix API.
 *
 * Lab Taxonomy 2026: matrix cells are keyed (UserKey × SkillKey) — the 53 lab
 * skills are the columns, grouped by section (Soil Testing - Index / Soil
 * Testing - Advanced / Rock Testing). Tests map N:1 onto skills via
 * TaskType.SkillKey on the taxonomy side.
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * Endpoints:
 *   GET    /api/skill-matrix              — full matrix (with optional filters)
 *   GET    /api/skill-matrix/users        — active user list
 *   GET    /api/skill-matrix/skills       — active skill list (column headers)
 *   PUT    /api/skill-matrix              — upsert one cell
 *   POST   /api/skill-matrix/bulk         — bulk apply one level to many users
 *   DELETE /api/skill-matrix              — clear one cell (BUG-2026-0075)
 *   POST   /api/skill-matrix/bulk-clear   — bulk clear cells (BUG-2026-0075)
 *   POST   /api/skill-matrix/batch        — heterogeneous cell batch
 */

import apiClient from './apiClient';
import { emitScheduleChange } from './scheduleBus';

// ── Types ────────────────────────────────────────────────────────────────────

export interface SkillMatrixCell {
  SkillMatrixKey: string | null;
  UserKey: string;
  UserDisplayName: string;
  UserEmail: string;
  JobClass: string | null;
  WorkGroup: string | null;
  Tier: string | null;
  SkillKey: string;
  SkillName: string;
  SkillCategory: string | null;
  Level: number | null;
}

export interface SkillUser {
  AppUserKey: string;
  DisplayName: string;
  Email: string;
  JobClass: string | null;
  WorkGroup: string | null;
  Tier: string | null;
}

/** One matrix column — a lab skill (Category = workbook section). */
export interface MatrixSkill {
  SkillKey: string;
  Name: string;
  Category: string | null;
  SortOrder: number;
}

export interface MatrixFilters {
  jobClass?: string;
  workGroup?: string;
}

export interface UpsertLevelRequest {
  UserKey: string;
  SkillKey: string;
  Level: number;
  Notes?: string;
}

export interface BulkApplyRequest {
  skillKey: string;
  userKeys: string[];
  level: number;
}

export interface ClearLevelRequest {
  UserKey: string;
  SkillKey: string;
}

export interface BulkClearRequest {
  skillKey: string;
  userKeys: string[];
}

/** One cell in a heterogeneous batch. Level=null means clear (soft-delete). */
export interface BatchCellUpdate {
  UserKey: string;
  SkillKey: string;
  Level: number | null;
}

export interface BatchUpsertResult {
  inserted: number;
  updated: number;
  deleted: number;
}

// ── Service functions ─────────────────────────────────────────────────────────

export async function fetchSkillMatrix(filters?: MatrixFilters): Promise<SkillMatrixCell[]> {
  const params: Record<string, string> = {};
  if (filters?.jobClass) params.jobClass = filters.jobClass;
  if (filters?.workGroup) params.workGroup = filters.workGroup;
  const response = await apiClient.get<SkillMatrixCell[]>('/skill-matrix', { params });
  return response.data;
}

export async function fetchSkillMatrixUsers(): Promise<SkillUser[]> {
  const response = await apiClient.get<SkillUser[]>('/skill-matrix/users');
  return response.data;
}

export async function fetchSkillMatrixSkills(): Promise<MatrixSkill[]> {
  const response = await apiClient.get<MatrixSkill[]>('/skill-matrix/skills');
  return response.data;
}

export async function upsertSkillLevel(data: UpsertLevelRequest): Promise<void> {
  await apiClient.put('/skill-matrix', data);
  // A changed skill level changes who's qualified + the level shown in the assignee
  // pickers — flush their cached user/skill lists (scheduleBus). (Gigi, 2026-06-22)
  emitScheduleChange({ kind: 'skills-changed', source: 'skill-matrix' });
}

export async function bulkApplySkillLevel(data: BulkApplyRequest): Promise<{ updated: number }> {
  const response = await apiClient.post<{ updated: number }>('/skill-matrix/bulk', data);
  emitScheduleChange({ kind: 'skills-changed', source: 'skill-matrix' });
  return response.data;
}

export interface SkillMatrixLookups {
  jobClasses: string[];
  workGroups: string[];
}

/**
 * Returns distinct JobClass and WorkGroup values from active AppUsers.
 * Powers the creatable Autocomplete filters in SkillMatrixToolbar.
 * Uses apiClient (MSAL auth + error capture) — not raw fetch.
 */
export async function fetchSkillMatrixLookups(): Promise<SkillMatrixLookups> {
  const { data } = await apiClient.get<SkillMatrixLookups>('/skill-matrix/lookups');
  return data;
}

/**
 * Clear a single skill cell back to "no skill recorded" (BUG-2026-0075).
 * Soft-deletes the SkillMatrix row server-side. Idempotent: clearing an
 * already-empty cell returns { cleared: false } and is not an error.
 */
export async function clearSkillLevel(data: ClearLevelRequest): Promise<{ cleared: boolean }> {
  const response = await apiClient.delete<{ cleared: boolean }>('/skill-matrix', { data });
  emitScheduleChange({ kind: 'skills-changed', source: 'skill-matrix' });
  return response.data;
}

/**
 * Bulk-clear skill levels for many users on one skill (BUG-2026-0075).
 * Returns the count of rows actually cleared.
 */
export async function bulkClearSkillLevel(data: BulkClearRequest): Promise<{ cleared: number }> {
  const response = await apiClient.post<{ cleared: number }>('/skill-matrix/bulk-clear', data);
  emitScheduleChange({ kind: 'skills-changed', source: 'skill-matrix' });
  return response.data;
}

/**
 * Batch upsert for a heterogeneous set of skill matrix cells.
 * Each cell can have its own UserKey, SkillKey, and Level.
 * Level=null clears the cell (soft-delete). Max 500 cells per call.
 *
 * Backed by a single MERGE statement on the server.
 * Uses apiClient (MSAL auth + error capture) — not raw fetch.
 */
export async function batchUpsertSkillLevels(
  cells: BatchCellUpdate[],
): Promise<BatchUpsertResult> {
  const { data } = await apiClient.post<BatchUpsertResult>('/skill-matrix/batch', { cells });
  emitScheduleChange({ kind: 'skills-changed', source: 'skill-matrix' });
  return data;
}

/**
 * Update a user's scheduling Tier (Lead / Senior / Tech / Junior; null clears
 * back to auto-derive-from-Job-Class). Admin-only server-side (PUT /users/:key).
 * Lives here because Tier is edited from the Skill Matrix meta column — it
 * drives Employee Schedule row grouping, never permissions (RBAC.md).
 */
export async function updateUserTier(appUserKey: string, tier: string | null): Promise<void> {
  await apiClient.put(`/users/${appUserKey}`, { tier });
  emitScheduleChange({ kind: 'skills-changed', source: 'skill-matrix' });
}
